import React from 'react';
import { Link } from 'react-router';
import {
  COMMAND_CENTER_ACTIVATION_TITLE,
  COMMAND_CENTER_AUTOPUBLISH_STAGE2_COPY,
  commandCenterActivationDescription,
} from '../../commandCenterCopy';
import { automationActivationStatusLabel } from '../../setupAutomationCopy';
import { lscPath } from '../../routing';
import type { LscAutomationActivationPayload } from '../../types';

export interface CommandCenterActivationStatusCardProps {
  leagueId: string;
  activation: LscAutomationActivationPayload | null;
  loading?: boolean;
}

function statusTone(status: string): string {
  if (status === 'active') return 'border-emerald-800/60 bg-emerald-950/20';
  if (status === 'pending_operator_enablement' || status === 'activation_requested' || status === 'entitlement_bridge_disabled') {
    return 'border-amber-700/40 bg-amber-950/10';
  }
  if (status === 'locked_unpaid') return 'border-red-700/40 bg-red-950/10';
  if (status === 'ready_to_activate') return 'border-primary-700/40 bg-primary-950/10';
  return 'border-secondary-700 bg-secondary-900/40';
}

const CommandCenterActivationStatusCard: React.FC<CommandCenterActivationStatusCardProps> = ({
  leagueId,
  activation,
  loading = false,
}) => {
  if (loading && !activation) {
    return (
      <div
        className="rounded-xl border border-secondary-700 bg-secondary-900/40 px-4 py-3 text-sm text-secondary-400"
        data-testid="command-center-activation-loading"
      >
        Loading activation status…
      </div>
    );
  }

  if (!activation) {
    return null;
  }

  const status = activation.activation_status;
  const activationHref = lscPath(`/leagues/${leagueId}/setup?step=automation_activation`);

  return (
    <div
      className={`rounded-xl border px-4 py-4 space-y-2 ${statusTone(status)}`}
      data-testid="command-center-activation-status"
    >
      <p className="text-xs uppercase tracking-wide text-secondary-500">{COMMAND_CENTER_ACTIVATION_TITLE}</p>
      <p className="text-sm font-medium text-secondary-100" data-testid="command-center-activation-status-label">
        {automationActivationStatusLabel(status)}
      </p>
      <p className="text-sm text-secondary-400">{commandCenterActivationDescription(status)}</p>
      {!activation.autopublish_enabled ? (
        <p className="text-xs text-secondary-500" data-testid="command-center-autopublish-off">
          {COMMAND_CENTER_AUTOPUBLISH_STAGE2_COPY}
        </p>
      ) : null}
      {status === 'ready_to_activate' || status === 'blocked' || status === 'locked_unpaid' ? (
        <Link
          to={activationHref}
          className="inline-flex text-sm font-medium text-primary-300 hover:text-primary-200"
          data-testid="command-center-activation-setup-link"
        >
          Review activation in setup wizard
        </Link>
      ) : null}
    </div>
  );
};

export default CommandCenterActivationStatusCard;
